import { ArrowRight, Clock3, MapPin, MessageCircle, Search, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HomepageTypewriterHeadline } from "@/components/landing/HomepageTypewriterHeadline";
import { useSiteBranding } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { formatDate, formatListingPrice } from "@/lib/portalHelpers";

const getPreviewThumbnail = (listing) => listing.image_url || listing.staged_image_url || listing.empty_image_url || (Array.isArray(listing.gallery_urls) ? listing.gallery_urls[0] : null) || "";
const AGENT_HERO_ROTATING_PHRASES = [
  "own branded portal",
  "Independent Brand",
  "Real-Time Analytics",
  "Lead Ownership",
  "Market Insights",
  "Investment Roadmaps",
  "AI Property Reports",
  "Automated Follow-Ups",
];

export const InstitutionalConversionHero = ({ buildBrandedPath, onSearchSubmit, onSeatCheckoutStart, uniqueCount = 139 }) => {
  const { getWhatsAppHref } = useSiteBranding();
  const [query, setQuery] = useState("");
  const [previewResults, setPreviewResults] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setPreviewResults([]);
      setPreviewLoading(false);
      return undefined;
    }

    let isActive = true;
    const timer = window.setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const response = await api.get("/listings/public", {
          params: {
            page: 1,
            page_size: 3,
            query: normalizedQuery,
          },
        });
        if (!isActive) return;
        setPreviewResults(Array.isArray(response.data?.items) ? response.data.items.slice(0, 3) : []);
      } catch {
        if (!isActive) return;
        setPreviewResults([]);
      } finally {
        if (isActive) setPreviewLoading(false);
      }
    }, 260);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const viewAllHref = (() => {
    const params = new URLSearchParams();
    params.set("unique_listings", "true");
    if (query.trim()) params.set("q", query.trim());
    return buildBrandedPath(`/listings?${params.toString()}`);
  })();

  return (
    <section className="section-shell pt-6" data-testid="institutional-conversion-hero">
      <div className="overflow-hidden rounded-[40px] border border-[#D4AF37]/18 bg-[linear-gradient(135deg,#07192f_0%,#102947_34%,#efe3c0_100%)] shadow-[0_40px_120px_rgba(0,25,53,0.28)]">
        <div className="grid lg:grid-cols-2">
          <div className="border-b border-white/10 px-8 py-9 text-white lg:border-b-0 lg:border-r lg:border-r-white/10 lg:px-10 lg:py-10" data-testid="institutional-hero-buyer-column">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#F5D98C]" data-testid="institutional-hero-buyer-label">Buyer Intelligence - For Buyers</p>
            <h2 className="mt-4 text-[1.65rem] font-semibold leading-tight md:text-[2.6rem]">Access thousands of properties. No Duplicate properties</h2>
            <p className="mt-4 max-w-xl text-sm leading-8 text-slate-200 md:text-base">
              Access properties throughout 28 districts without the duplicate clutter. Instant WhatsApp handshakes between buyers and agents. Real-time asset valuations and signal decoding active. Exclusive access to the market's most powerful automation suite.
            </p>
            <Link className="gold-button mt-8 inline-flex" data-testid="institutional-hero-find-home-button" to={buildBrandedPath("/listings?unique_listings=true") }>
              Find Your Home <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="bg-[linear-gradient(135deg,rgba(255,250,241,0.96)_0%,rgba(246,236,218,0.94)_100%)] px-8 py-9 text-[#001935] lg:px-10 lg:py-10" data-testid="institutional-hero-agent-column">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="institutional-hero-agent-label">Agent Conversion - For Agents</p>
            <HomepageTypewriterHeadline
              className="mt-4 text-[1.65rem] font-semibold leading-tight md:text-[2.6rem]"
              phrases={AGENT_HERO_ROTATING_PHRASES}
              prefix="Your all-in-one platform for"
              prefixClassName="text-[#001935]"
              rotatingClassName="inline-block min-h-[1.15em] align-baseline text-[#8b6d16]"
              testIdPrefix="institutional-hero-agent-typewriter"
            />
            <p className="mt-4 max-w-xl text-sm leading-8 text-[#42566f] md:text-base">
              Get your personal branded website for your real estate business. Showcase your property listings. Stop buying leads (that too shared) and generate & own your exclusive leads. Close deals faster with our AI automations and tools, inbuilt in your own branded website & market's most powerful automation suite.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="institutional-hero-pricing-clarity-row">
              {[
                "Plans from S$129/mo",
                "Most founders start on S$249/mo",
                "Upgrade to team scale when ready",
              ].map((item) => (
                <span className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`institutional-hero-pricing-clarity-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item}>
                  {item}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                className="gold-button"
                data-testid="institutional-hero-signature-intelligence-button"
                href={getWhatsAppHref("Hello SG Prop Portal, I want to discuss a Founding Seat.")}
                rel="noreferrer"
                target="_blank"
              >
                Talk to Signature Intelligence <MessageCircle className="ml-2 h-4 w-4" />
              </a>
              <button className="outline-button" data-testid="institutional-hero-secure-seat-button" onClick={onSeatCheckoutStart} type="button">
                Subscribe to Pro Plan <Sparkles className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-[rgba(0,18,36,0.86)] px-6 py-5 lg:px-8" data-testid="institutional-hero-search-shell">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D98C]">Integrated Search</p>
              <p className="mt-2 text-sm text-slate-200">Search by signal, project, or district.</p>
            </div>
            <div className="flex-1 lg:max-w-3xl">
              <form
                className="grid gap-3 lg:grid-cols-[1fr_auto]"
                data-testid="institutional-hero-search-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  onSearchSubmit?.(query);
                }}
              >
                <label className="sr-only" htmlFor="institutional-hero-search-input">
                  Search by signal, project, or district.
                </label>
                <input
                  className="w-full rounded-[22px] border border-[#D4AF37]/26 bg-white px-5 py-4 text-base text-[#001935] outline-none transition focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/10"
                  data-testid="institutional-hero-search-input"
                  id="institutional-hero-search-input"
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by signal, project, or district."
                  value={query}
                />
                <button className="gold-button !justify-center" data-testid="institutional-hero-search-submit-button" type="submit">
                  <Search className="mr-2 h-4 w-4" /> Search the Marketplace
                </button>
              </form>

              {query.trim().length >= 2 ? (
                <div className="mt-4 rounded-[26px] border border-white/10 bg-[rgba(255,255,255,0.06)] p-3" data-testid="institutional-hero-live-preview">
                  <div className="flex items-center justify-between gap-3 px-2 pb-2">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5D98C]">Live Result Preview</p>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-300" data-testid="institutional-hero-live-preview-status">
                      {previewLoading ? "Searching..." : `${previewResults.length} matched properties`}
                    </p>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-3">
                    {previewResults.map((listing) => (
                      <Link
                        className="rounded-[22px] border border-[#D4AF37]/18 bg-white/95 p-4 text-[#001935] shadow-[0_16px_45px_rgba(0,25,53,0.1)] transition hover:-translate-y-0.5 hover:border-[#D4AF37]/45"
                        data-testid={`institutional-hero-live-preview-card-${listing.id}`}
                        key={listing.id}
                        to={buildBrandedPath(`/listings/${listing.id}`)}
                      >
                        {getPreviewThumbnail(listing) ? (
                          <div className="overflow-hidden rounded-[18px] border border-[#001935]/8 bg-[#f5efe2]" data-testid={`institutional-hero-live-preview-thumbnail-${listing.id}`}>
                            <img
                              alt={listing.title}
                              className="h-28 w-full object-cover"
                              loading="lazy"
                              src={getPreviewThumbnail(listing)}
                            />
                          </div>
                        ) : (
                          <div className="flex h-28 items-center justify-center rounded-[18px] border border-dashed border-[#D4AF37]/25 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_100%)] text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`institutional-hero-live-preview-thumbnail-placeholder-${listing.id}`}>
                            Signature Preview
                          </div>
                        )}
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{listing.category === "new_launch" ? "Project Match" : "Live Match"}</p>
                        <h3 className="mt-2 text-lg font-semibold leading-tight text-[#001935]">{listing.title}</h3>
                        <p className="mt-3 inline-flex items-center gap-2 text-sm text-[#42566f]">
                          <MapPin className="h-4 w-4 text-[#D4AF37]" /> {listing.district || listing.address}
                        </p>
                        <p className="mt-4 text-base font-semibold text-[#001935]">{formatListingPrice(listing)}</p>
                        <p className="mt-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#42566f]">
                          <Clock3 className="h-3.5 w-3.5 text-[#8b6d16]" /> Recently Updated {formatDate(listing.updated_at)}
                        </p>
                      </Link>
                    ))}
                  </div>
                  {!previewLoading && !previewResults.length ? (
                    <div className="px-2 py-4 text-sm text-slate-200" data-testid="institutional-hero-live-preview-empty-state">
                      No instant matches yet — press Search to explore the full marketplace.
                    </div>
                  ) : null}
                  <div className="px-2 pt-4">
                    <Link
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#F5D98C] transition hover:text-white"
                      data-testid="institutional-hero-view-all-opportunities-link"
                      to={viewAllHref}
                    >
                      View all {uniqueCount} opportunities <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};